import type { ClientMessage } from '@roll1/shared';
import type { DistributiveOmit } from '../../hooks/useMultiplayer';
import { useMemo, useState } from 'react';
import type { JSX, MutableRefObject } from 'react';
import {
  ArrowDown,
  ArrowUp,
  Bot,
  Camera,
  Eraser,
  FolderInput,
  Move,
  LogOut,
  MoreHorizontal,
  Pencil,
  Trash2,
} from 'lucide-react';
import { useOptionalSessionContext } from '../../contexts/SessionContext.js';
import { useI18n } from '../../i18n/index.js';
import DeletePageDialog from '../DeletePageDialog';
import { Button } from '../ui/button';
import { useAskDialogs } from './useAskDialogs.js';
import { ImagePositionDialog, NestMapDialog, RenameMapDialog } from './MapDialogs.js';
import { buildTreeOrder, descendantIds } from '../../lib/page-tree.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

/** What the map pane (the canvas inside it) lets the header's menu do: the parts that need the pane's own providers. */
export interface StudioMapControl {
  readonly clearDrawings: () => void;
  readonly openAiMap: () => void;
  /** Drag the image with the mouse on this map; Esc leaves. */
  readonly startMoveImage: () => void;
}

export interface StudioMapMenuProps {
  readonly pageId: string;
  readonly pageName: string;
  /** Sibling snapshots of the same location (includes this page). */
  readonly snapshots?: readonly { id: string; name: string }[];
  readonly onSnapshot?: (label: string) => void;
  readonly onFocusSnapshot?: (pageId: string) => void;
  /** «Открыть у себя»: leave Studio with a GM preview of this map. */
  readonly onOpenWorkshop: () => void;
  /** The map pane's own actions; filled by StudioMapCanvas. */
  readonly controlRef?: MutableRefObject<StudioMapControl | null>;
  /** Only a live map (with a canvas) has image position, AI map and drawings. */
  readonly isMap: boolean;
}

/**
 * The «⋯» of a Studio map (package 5.4, P06 P-3): everything that is done TO the map lives here —
 * the table has no map management (T-1). Rename, order among its neighbours, nesting, delete (with
 * the players' destination when the map is theirs), AI map, clear drawings, the image's position —
 * next to the older «Открыть у себя» and the location snapshots.
 */
export function StudioMapMenu({ pageId, pageName, snapshots, onSnapshot, onFocusSnapshot, onOpenWorkshop, controlRef, isMap }: StudioMapMenuProps): JSX.Element {
  const { t } = useI18n();
  const session = useOptionalSessionContext();
  const [renameOpen, setRenameOpen] = useState<boolean>(false);
  const [nestOpen, setNestOpen] = useState<boolean>(false);
  const [positionOpen, setPositionOpen] = useState<boolean>(false);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);

  const pages = session?.pages.pages ?? [];
  const page = pages.find((p) => p.id === pageId);
  const siblings = useMemo(
    () => (page === undefined ? [] : buildTreeOrder(pages).filter((p) => p.parentPageId === page.parentPageId)),
    [pages, page],
  );
  const siblingIndex = siblings.findIndex((p) => p.id === pageId);
  const send = (message: DistributiveOmit<ClientMessage, 'id' | 'timestamp'>): void => {
    if (session?.mp.session) session.mp.send(message);
  };

  // The order of every map of the campaign (what page:reorder sets): a map has a neighbour above / below
  // among its siblings, or — a map alone under its parent — in the campaign's whole list.
  const flat = useMemo(() => [...pages].sort((a, b) => a.sortOrder - b.sortOrder), [pages]);
  const flatIndex = flat.findIndex((p) => p.id === pageId);
  const hasNeighbour = (direction: -1 | 1): boolean => {
    const sibling = siblingIndex + direction;
    if (sibling >= 0 && sibling < siblings.length) return true;
    const at = flatIndex + direction;
    return flatIndex >= 0 && at >= 0 && at < flat.length;
  };
  const move = (direction: -1 | 1): void => {
    const sibling = siblings[siblingIndex + direction];
    if (sibling !== undefined) {
      // Swap the two sibling maps in the tree order; the maps nested into them travel with them.
      const ordered = buildTreeOrder(pages);
      const a = ordered.findIndex((p) => p.id === pageId);
      const b = ordered.findIndex((p) => p.id === sibling.id);
      [ordered[a], ordered[b]] = [ordered[b]!, ordered[a]!];
      send({ type: 'page:reorder', pageIds: ordered.map((p) => p.id) });
      return;
    }
    const neighbour = flat[flatIndex + direction];
    if (neighbour === undefined) return;
    const ordered = [...flat];
    [ordered[flatIndex], ordered[flatIndex + direction]] = [neighbour, ordered[flatIndex]!];
    send({ type: 'page:reorder', pageIds: ordered.map((p) => p.id) });
  };

  const nestOptions = useMemo(() => {
    const blocked = descendantIds(pageId, pages);
    return pages.filter((p) => p.id !== pageId && !blocked.has(p.id)).map((p) => ({ id: p.id, name: p.name }));
  }, [pages, pageId]);

  const others = (snapshots ?? []).length > 1;
  const control = (): StudioMapControl | null => controlRef?.current ?? null;
  const managed = session !== null && page !== undefined;
  const { askName, dialogs: askDialogs } = useAskDialogs();

  return (
    <>
      {askDialogs}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="flex-none"
            data-testid="page-map-menu"
            aria-label={t('studio.mapMenu.label')}
            title={t('studio.mapMenu.label')}
          >
            <MoreHorizontal size={14} aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72" data-testid="page-map-menu-content">
          <DropdownMenuItem
            className="page-entity-view__workshop-button"
            data-testid="page-open-at-table"
            title={t('studio.entity.openWorkshopHint')}
            onSelect={onOpenWorkshop}
          >
            <LogOut aria-hidden="true" />
            {t('studio.entity.openWorkshop')}
          </DropdownMenuItem>
          {managed ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={(): void => setRenameOpen(true)}>
                <Pencil aria-hidden="true" />
                {t('studio.mapMenu.rename')}
              </DropdownMenuItem>
              <DropdownMenuItem disabled={!hasNeighbour(-1)} onSelect={(): void => move(-1)}>
                <ArrowUp aria-hidden="true" />
                {t('studio.mapMenu.moveUp')}
              </DropdownMenuItem>
              <DropdownMenuItem disabled={!hasNeighbour(1)} onSelect={(): void => move(1)}>
                <ArrowDown aria-hidden="true" />
                {t('studio.mapMenu.moveDown')}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={(): void => setNestOpen(true)}>
                <FolderInput aria-hidden="true" />
                {t('studio.mapMenu.nest')}
              </DropdownMenuItem>
              {isMap ? (
                <>
                  <DropdownMenuItem onSelect={(): void => setPositionOpen(true)}>
                    <Move aria-hidden="true" />
                    {t('studio.mapMenu.imagePosition')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={(): void => control()?.openAiMap()}>
                    <Bot aria-hidden="true" />
                    {t('studio.mapMenu.aiMap')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={(): void => control()?.clearDrawings()}>
                    <Eraser aria-hidden="true" />
                    {t('studio.mapMenu.clearDrawings')}
                  </DropdownMenuItem>
                </>
              ) : null}
              <DropdownMenuItem
                variant="destructive"
                disabled={pages.length <= 1}
                onSelect={(): void => setDeleteOpen(true)}
              >
                <Trash2 aria-hidden="true" />
                {t('studio.mapMenu.delete')}
              </DropdownMenuItem>
            </>
          ) : null}
          {onSnapshot !== undefined || others ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuGroup data-testid="page-snapshots" aria-label={t('studio.entity.snapshots')}>
                <DropdownMenuLabel className="text-label font-semibold uppercase tracking-label text-dim">
                  {t('studio.entity.snapshots')}
                </DropdownMenuLabel>
                {others ? (
                  <DropdownMenuRadioGroup value={pageId} onValueChange={(id: string): void => onFocusSnapshot?.(id)}>
                    {(snapshots ?? []).map((s) => (
                      <DropdownMenuRadioItem key={s.id} value={s.id} data-testid={`page-snapshot-${s.id}`}>
                        <span className="truncate">{s.name}</span>
                      </DropdownMenuRadioItem>
                    ))}
                  </DropdownMenuRadioGroup>
                ) : (
                  <p className="px-2 py-1 text-meta text-muted-foreground">{t('studio.mapMenu.noSnapshots')}</p>
                )}
                {onSnapshot !== undefined ? (
                  <DropdownMenuItem
                    data-testid="page-snapshot-create"
                    title={t('studio.entity.snapshotCreateHint')}
                    onSelect={(): void => {
                      void askName(t('studio.entity.snapshotPrompt'), pageName, { maxLength: 50 }).then((label): void => {
                        if (label !== null) onSnapshot(label);
                      });
                    }}
                  >
                    <Camera aria-hidden="true" />
                    {t('studio.entity.snapshotCreate')}
                  </DropdownMenuItem>
                ) : null}
              </DropdownMenuGroup>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>

      {managed ? (
        <>
          <RenameMapDialog
            open={renameOpen}
            name={page.name}
            t={t}
            onSave={(name): void => send({ type: 'page:rename', pageId, name })}
            onClose={(): void => setRenameOpen(false)}
          />
          <NestMapDialog
            open={nestOpen}
            parentId={page.parentPageId}
            options={nestOptions}
            t={t}
            onSave={(parentPageId): void => send({ type: 'page:set-parent', pageId, parentPageId })}
            onClose={(): void => setNestOpen(false)}
          />
          <ImagePositionDialog
            open={positionOpen}
            x={session.pages.getPageData(pageId)?.mapOffsetX ?? 0}
            y={session.pages.getPageData(pageId)?.mapOffsetY ?? 0}
            t={t}
            onSave={({ x, y }): void => send({ type: 'page:update-settings', pageId, changes: { mapOffsetX: x, mapOffsetY: y } })}
            onMoveWithMouse={(): void => control()?.startMoveImage()}
            onClose={(): void => setPositionOpen(false)}
          />
          <DeletePageDialog
            page={deleteOpen ? page : null}
            pages={pages}
            activePageId={session.pages.activePageId}
            hasChildren={pages.some((p) => p.parentPageId === pageId)}
            onConfirm={(id, movePlayersTo): void =>
              send({ type: 'page:delete', pageId: id, ...(movePlayersTo !== undefined ? { movePlayersTo } : {}) })
            }
            onClose={(): void => setDeleteOpen(false)}
          />
        </>
      ) : null}
    </>
  );
}

export default StudioMapMenu;
